import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrdine, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert, Table } from 'react-bootstrap';
import '../print.css';

function OrdiniPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ordine, setOrdine] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrdineAndAziendaData = async () => {
      setLoading(true);
      setError(null);
      try {
      
        const [ordineResponse, aziendaResponse] = await Promise.all([
          getOrdine(id),
          getDatiAzienda()
        ]);


        setOrdine(ordineResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error("Errore dettagliato nel caricamento:", err);
        if (err.response) {
          console.error("Dettagli errore response:", err.response);
        }
      } finally {
        setLoading(false);

      }
    };
    fetchOrdineAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/ordini');
      }, 100); // 100ms delay
    };

    if (!loading && !error && ordine && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, ordine, datiAzienda, navigate]);



  const renderField = (label, value) => (
    <Row className="mb-2">
      <Col xs={5}><span className="label">{label}:</span></Col>
      <Col xs={7}><span className="value">{value || 'N/A'}</span></Col>
    </Row>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione stampa ordine...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <p>Impossibile preparare la stampa.</p>
      </Container>
    );
  }

  if (!ordine || !datiAzienda) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Dati dell'ordine o dell'azienda non trovati per la stampa.</Alert>
      </Container>
    );
  }

  return (
    <div id="printable-area" className="portrait-page">
      <div className="print-container">
        <header className="print-header">
          <div className="company-details">
            {datiAzienda.logo_url && (
              <img
                src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                alt="Logo Azienda"
                style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="print-info">
            <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>Ordine #:</strong> {ordine.id}</p>
          </div>
        </header>

        <main>
          <h2 className="document-title">Riepilogo Ordine</h2>

          <section className="print-section">
            <div className="print-section-title">Dettagli Ordine</div>
            <div className="print-section-content">
              <Row>
                <Col md={6}>
                  {renderField('Data Ordine', new Date(ordine.data_ordine).toLocaleDateString())}
                  {renderField('Cliente', ordine.cliente ? `${ordine.cliente.nome} ${ordine.cliente.cognome}` : 'N/A')}
                </Col>
                <Col md={6}>
                  {renderField('Corriere', ordine.corriere ? ordine.corriere.denominazione : 'N/A')}
                  {renderField('Metodo Pagamento', ordine.metodo_pagamento)}
                </Col>
              </Row>
            </div>
          </section>

          <section className="print-section">
            <div className="print-section-title">Prodotti Ordinati</div>
            <div className="print-section-content" style={{ padding: '0' }}>
              <Table className="print-table mb-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Titolo</th>
                    <th className="text-right">Q.tà</th>
                    <th className="text-right">Prezzo Un.</th>
                    <th className="text-right">IVA %</th>
                    <th className="text-right">Subtotale</th>
                  </tr>
                </thead>
                <tbody>
                  {ordine.dettagli.map(dettaglio => {
                    let productSku = 'N/A';
                    let productTitle = 'Prodotto non trovato';
                    let prezzoUnitarioEffettivo = dettaglio.prezzo_unitario;
                    let ivaPercentualeEffettiva = dettaglio.iva_percentuale;

                    if (dettaglio.prodotto_libero) {
                      productTitle = dettaglio.descrizione_prodotto_libero;
                      prezzoUnitarioEffettivo = dettaglio.prezzo_unitario_libero;
                      ivaPercentualeEffettiva = dettaglio.iva_percentuale_libero;
                      productSku = 'N/A'; // Ad-hoc products don't have an SKU
                    } else if (dettaglio.film) {
                      productSku = dettaglio.film.sku || 'N/A';
                      productTitle = dettaglio.film.titolo;
                    } else if (dettaglio.musica) {
                      productSku = dettaglio.musica.sku || 'N/A';
                      productTitle = dettaglio.musica.titolo;
                    } else if (dettaglio.servizio) {
                      productSku = dettaglio.servizio.sku || 'N/A';
                      productTitle = dettaglio.servizio.descrizione;
                    }

                    return (
                      <tr key={dettaglio.id}>
                        <td>{productSku}</td>
                        <td>{productTitle}</td>
                        <td className="text-right">{dettaglio.quantita}</td>
                        <td className="text-right">€ {prezzoUnitarioEffettivo.toFixed(2)}</td>
                        <td className="text-right">{ivaPercentualeEffettiva}%</td>
                        <td className="text-right">€ {(dettaglio.quantita * prezzoUnitarioEffettivo).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </section>

          <div className="totals-section">
            <table className="totals-table">
              <tbody>
                <tr>
                  <td className="label">Subtotale Imponibile:</td>
                  <td className="value">€ {ordine.subtotale_imponibile.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="label">Totale IVA:</td>
                  <td className="value">€ {ordine.totale_iva.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="label">Spese Trasporto:</td>
                  <td className="value">€ {ordine.spese_trasporto.toFixed(2)}</td>
                </tr>
                {ordine.sconto > 0 && (
                  <tr>
                    <td className="label">Sconto:</td>
                    <td className="value">- € {ordine.sconto.toFixed(2)}</td>
                  </tr>
                )}
                {ordine.acconto > 0 && (
                  <tr>
                    <td className="label">Acconto:</td>
                    <td className="value">- € {ordine.acconto.toFixed(2)}</td>
                  </tr>
                )}
                <tr>
                  <td className="label" style={{ fontSize: '1.2em' }}>Totale Ordine:</td>
                  <td className="value" style={{ fontSize: '1.2em', fontWeight: 'bold' }}>€ {ordine.totale_ordine.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </main>

        <footer className="print-footer">
          <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
          <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
        </footer>
      </div>
    </div>
  );
}

export default OrdiniPrint;